import { z } from 'zod';
import { totpCodeSchema } from './owner';
export const EMAIL_KINDS = ['appointments', 'results', 'billing', 'staff_alerts', 'messages', 'test'];
export const SWITCHABLE_EMAIL_KINDS = EMAIL_KINDS.filter((kind) => kind !== 'test');
export const SMTP_ENCRYPTIONS = ['NONE', 'STARTTLS', 'SSL'];
export const EMAIL_LOG_STATUSES = ['sent', 'failed', 'skipped'];
export const EMAIL_VIAS = ['tenant_smtp', 'owner_smtp'];
export const EMAIL_PAUSE_REASONS = ['daily_limit', 'monthly_limit'];
export const DEFAULT_TENANT_EMAILS_PER_DAY = 200;
export const DEFAULT_TENANT_EMAILS_PER_MONTH = 3_000;
export const EMAIL_EXTRA_PACK_SIZE = 1_000;
export const EMAIL_EXTRA_PACKS_MAX = 100;
export const EMAIL_LIMIT_MAX = 10_000_000;
export const EMAIL_LOG_PAGE_MAX = 100;
const TEMPLATE_KINDS = {
    appointment_booked: 'appointments',
    appointment_reminder: 'appointments',
    appointment_cancelled: 'appointments',
    appointment_rescheduled: 'appointments',
    followup_reminder: 'appointments',
    tele_link: 'appointments',
    report_ready: 'results',
    critical_result: 'results',
    critical_result_patient: 'results',
    discharge_instructions: 'results',
    payment_receipt: 'billing',
    deposit_low: 'billing',
    claim_overdue: 'billing',
    portal_otp: 'messages',
    low_stock: 'staff_alerts',
    licence_expiry: 'staff_alerts',
};
export function emailKindOfTemplate(templateCode) {
    if (!templateCode)
        return 'messages';
    return TEMPLATE_KINDS[templateCode] ?? 'staff_alerts';
}
export const emailSwitchMapSchema = z
    .object(Object.fromEntries(SWITCHABLE_EMAIL_KINDS.map((kind) => [kind, z.boolean().optional()])))
    .strict();
export function parseEmailSwitches(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return {};
    const out = {};
    for (const kind of SWITCHABLE_EMAIL_KINDS) {
        const entry = value[kind];
        if (typeof entry === 'boolean')
            out[kind] = entry;
    }
    return out;
}
export function emailKindSwitchedOff(kind, global, tenant) {
    if (kind === 'test')
        return null;
    if (global[kind] === false)
        return 'global';
    if (tenant[kind] === false)
        return 'tenant';
    return null;
}
export function computeEmailQuota(input) {
    const dailyLimit = Math.max(0, input.dailyLimit);
    const monthlyLimit = Math.max(0, input.monthlyLimit);
    const extraThisMonth = Math.max(0, input.extraThisMonth);
    const monthlyAllowance = monthlyLimit > 0 ? monthlyLimit + extraThisMonth : 0;
    const dailyLeft = dailyLimit > 0 ? Math.max(0, dailyLimit - input.usedToday) : null;
    const monthlyLeft = monthlyAllowance > 0 ? Math.max(0, monthlyAllowance - input.usedThisMonth) : null;
    return {
        dailyLimit,
        monthlyLimit,
        extraThisMonth,
        monthlyAllowance,
        usedToday: input.usedToday,
        usedThisMonth: input.usedThisMonth,
        dailyLeft,
        monthlyLeft,
        exhaustedBy: monthlyLeft === 0 ? 'monthly_limit' : dailyLeft === 0 ? 'daily_limit' : null,
    };
}
export const saveTenantSmtpSchema = z.object({
    host: z.string().trim().min(1).max(253).regex(/^[A-Za-z0-9.:\-[\]]+$/, 'Enter a host name such as smtppro.zoho.com'),
    port: z.number().int().min(1).max(65535),
    username: z.string().trim().max(254).default(''),
    password: z.string().max(500).optional(),
    encryption: z.enum(SMTP_ENCRYPTIONS),
    fromEmail: z.string().trim().email().max(254),
    fromName: z.string().trim().max(120).default(''),
});
export const testTenantSmtpSchema = z.object({ toEmail: z.string().trim().email().max(254) });
export const emailLogQuerySchema = z.object({
    status: z.enum(EMAIL_LOG_STATUSES).optional(),
    limit: z.coerce.number().int().min(1).max(EMAIL_LOG_PAGE_MAX).default(50),
    cursor: z.string().uuid().optional(),
});
export const ownerEmailSettingsRequestSchema = z.object({
    tenantEmailsPerDay: z.number().int().min(0).max(EMAIL_LIMIT_MAX).optional(),
    tenantEmailsPerMonth: z.number().int().min(0).max(EMAIL_LIMIT_MAX).optional(),
    switches: emailSwitchMapSchema.optional(),
    totpCode: totpCodeSchema,
});
export const ownerTenantEmailRequestSchema = z
    .object({
    dailyLimit: z.number().int().min(0).max(EMAIL_LIMIT_MAX).nullable().optional(),
    monthlyLimit: z.number().int().min(0).max(EMAIL_LIMIT_MAX).nullable().optional(),
    addPacks: z.number().int().min(1).max(EMAIL_EXTRA_PACKS_MAX).optional(),
    ownerSmtpAllowed: z.boolean().optional(),
    switches: emailSwitchMapSchema.optional(),
    totpCode: totpCodeSchema,
})
    .refine((body) => body.dailyLimit !== undefined ||
    body.monthlyLimit !== undefined ||
    body.addPacks !== undefined ||
    body.ownerSmtpAllowed !== undefined ||
    body.switches !== undefined, { message: 'Nothing to change' });
export const ownerEmailResumeSchema = z.object({ totpCode: totpCodeSchema });
//# sourceMappingURL=email.js.map